import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import placesService from '../services/placesService';
import {PlaceListing} from '../types/Place';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {CONFIG} from "../../../common/config/settings";
import {handleError} from "../../../common/utils/errorHandling";
import {useToast} from "../../../common/contexts/ToastContext";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";

const PlacesList: React.FC = () => {
  const {t} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [places, setPlaces] = useState<PlaceListing[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    type: {value: null, matchMode: FilterMatchMode.CONTAINS},
    'churchUnit.name': {value: null, matchMode: FilterMatchMode.CONTAINS}
  });

  useEffect(() => {
    fetchPlaces();
  }, []);

  const fetchPlaces = async (): Promise<void> => {
    const placesData = await placesService.getAllPlaces();
    setPlaces(placesData);
  };

  const handleDelete = async (place: PlaceListing): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: place.name}),
      accept: async () => {
        await handleError(
          async () => {
            await placesService.deletePlace(place.id);
            await fetchPlaces();
          },
          t('admin.places.errors.deleting'),
          undefined,
          showError
        );
      }
    });
  };

  const handleEdit = (place: PlaceListing) => {
    navigate(`${place.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.places.title.list')}
        subtitle={t('admin.places.subtitle.list')}
      />

      <AdminButton
        onClick={handleCreateNew}
        variant="black"
        label={t('admin.places.actions.createNew')}
      />

      <DataTable
        value={places}
        stripedRows
        rows={CONFIG.admin.tables.rowsCount}
        paginator
        filterDisplay="row"
        filters={filters}
        // columnResizeMode="fit"
        // resizableColumns
        showGridlines
      >
        <Column
          sortField="name"
          header={t('admin.common.columns.name')}
          sortable
          filter
          filterField="name"
          filterPlaceholder={t('admin.common.filters.searchByName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(place: PlaceListing) => (
            <strong>{place.name}</strong>
          )}
        />
        <Column
          sortField="churchUnit.name"
          header={t('admin.common.columns.churchUnit')}
          sortable
          filter
          filterField="churchUnit.name"
          filterPlaceholder={t('admin.common.filters.searchByChurchUnit')}
          showFilterMenu={false}
          showClearButton={false}
          body={(place: PlaceListing) => place.churchUnit?.name ?? ''}
        />
        <Column header={t('admin.common.columns.actions')} body={(place: PlaceListing) => (
          <div>
            <Button
              onClick={() => handleEdit(place)}
              icon="fa fa-edit"
              severity="info"
              size="small"
              outlined
              className="mr-2"
              aria-label={t('admin.common.actions.edit')}
              tooltip={t('admin.common.actions.edit')}
              tooltipOptions={{position: 'top'}}
            />
            <Button
              onClick={() => handleDelete(place)}
              icon="fa fa-trash"
              severity="danger"
              size="small"
              outlined
              aria-label={t('admin.common.actions.delete')}
              tooltip={t('admin.common.actions.delete')}
              tooltipOptions={{position: 'top'}}
            />
          </div>)}/>
      </DataTable>
    </>
  );
};

export default PlacesList;

